import React from 'react'

import { HighlightsWidgetItemValueStyles } from 'features/views/LayoutEditor/widgets/HighlightsWidget/HighlightsWidget.css'

import { Box } from 'ui/components/Box'
import { Icon } from 'ui/components/Icon'
import { Body } from 'ui/components/Text'

import { useBaseAttributeState } from './hooks/useBaseAttributeState'
import { HighlightsWidgetAttributeProps } from './types'

const sizeMapping = {
    fontSize: {
        m: 'bodyM',
        l: 'bodyL',
    },
    lineHeight: {
        m: 'bodyM',
        l: 'bodyL',
    },
    labelGap: {
        m: '2xs',
        l: 0,
    },
} as const

type BaseAttributeProps = React.ComponentPropsWithoutRef<typeof Box> &
    HighlightsWidgetAttributeProps & {
        isEmpty?: boolean
    }

export const BaseAttribute = React.forwardRef<HTMLDivElement, BaseAttributeProps>(
    function BaseAttribute(
        { item, isEmpty, children, size, showIcon, showLabel, displayStyle, ...props },
        ref
    ) {
        const { fieldIcon, label } = useBaseAttributeState({
            item,
        })

        const fontSize = sizeMapping.fontSize[size]
        const lineHeight = sizeMapping.lineHeight[size]
        const labelGap = sizeMapping.labelGap[size]

        const effectiveShowIcon = showIcon && !!fieldIcon

        return (
            <Box
                ref={ref}
                flex
                alignItems={showLabel ? 'flex-start' : 'center'}
                fontFamily="body"
                fontSize={fontSize}
                fontWeight="bodyBold"
                lineHeight={lineHeight}
                color="text"
                trim
                gap="m"
                {...props}
            >
                {effectiveShowIcon && (
                    <Box noShrink>
                        <Icon {...fieldIcon} size="xl" color="icon" />
                    </Box>
                )}
                <Box flex column grow shrink gap={labelGap}>
                    {showLabel && (
                        <Box grow shrink trim maxWidth="full">
                            <Body size="m" color="textWeaker">
                                {label}
                            </Body>
                        </Box>
                    )}
                    <Box
                        className={HighlightsWidgetItemValueStyles.styleFunction({
                            style: displayStyle,
                        })}
                    >
                        {isEmpty ? (
                            <Body
                                color="textWeakest"
                                style={{
                                    fontSize: 'inherit',
                                    lineHeight: 'inherit',
                                    letterSpacing: 'inherit',
                                    fontWeight: 'inherit',
                                    fontStyle: 'italic',
                                }}
                                maxWidth="full"
                                noShrink
                                trim
                                display="block"
                            >
                                None
                            </Body>
                        ) : (
                            children
                        )}
                    </Box>
                </Box>
            </Box>
        )
    }
)
